import { useAppSettings } from "@@/settings";
import { apiFetchClient } from "@@/shared/fetch-client";
import { useMutation } from "@tanstack/react-query";
import type {
    AccessKeyId,
    OrderGroupId,
    VerifyStripePaymentCommand,
} from "@towni/common";
import { verifyStripePaymentCommandFactory } from "@towni/common";
import { useEffect } from "react";

/**
 * Data required to verify a Stripe payment.
 */
type VerifyData = {
    /**
     * The Stripe payment verification command.
     */
    data: VerifyStripePaymentCommand;
    /**
     * The access key ID to use for the request.
     */
    ak?: AccessKeyId;
};

/**
 * Posts a Stripe payment verification command to the API.
 * @param data The data required to verify the payment.
 * @returns A Promise that resolves to the verification result.
 */
const postVerifyStripePaymentCommand = (data: VerifyData) => {
    return apiFetchClient.post<VerifyStripePaymentCommand>({
        route: `/commands/verify-stripe-payment${
            data.ak ? `?ak=${data.ak}` : ""
        }`,
        body: data.data,
    });
};

/**
 * A hook that verifies a Stripe payment for a given order group ID and access key ID.
 * @param orderGroupId The ID of the order group to verify the payment for.
 * @param ak The access key ID to use for the request.
 */
const useVerifyStripePayment = (
    orderGroupId: OrderGroupId,
    ak: AccessKeyId | undefined,
): void => {
    const verifyStripePaymentCommandMutation = useMutation({
        mutationFn: postVerifyStripePaymentCommand,
    });

    const settings = useAppSettings();

    useEffect(() => {
        //Adding a delay to ensure the Stripe payment verification command is not sent immediately
        //As this is a fallback and often we get a webhook from Stripe before this runs
        const timeoutLength = settings.isLocalEnv ? 50 : 5000;
        const timer = setTimeout(() => {
            verifyStripePaymentCommandMutation.mutate({
                data: verifyStripePaymentCommandFactory({
                    orderGroupId,
                }),
                ak,
            });
        }, timeoutLength); // 5 second delay

        // Cleanup function to clear timeout if component unmounts or dependencies change
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ak, orderGroupId]);
};

export { useVerifyStripePayment };
